import {
  Component,
  OnInit,
  OnDestroy,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  ViewChild, AfterViewInit,
} from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';

import { tap, filter, Subscription, take, catchError, of, concatMap, switchMap } from 'rxjs';

import { Location } from '@shared/location/models';
import { Account } from '@shared/account/models';
import { DefaultPage } from '@shared/restaurant/enums';
import { FrontendModalService } from '@tablesready/frontend-modal';
import { AuthRestaurantService } from '@tablesready/frontend-store';
import { AlertsService, IAlertTypes } from '@tablesready/frontend-alerts-bar';
import { FrontendDropdownPlainComponent } from '@tablesready/frontend-dropdown';
import { NavigationService } from '@tablesready/common-ui';
import {
  CUSTOMER_LABEL_OPTIONS,
  QUOTED_LABEL_OPTIONS,
  AUTO_REMOVE_OPTIONS,
  getWaitFieldsOptions,
  NOTIFICATION_TIME_OPTIONS,
  DEFAULT_PAGE_OPTIONS,
} from './waitlist-options.constant';
import { QuotedTimeModal } from './components/quoted-time-modal/quoted-time-modal.component';
import {
  WhoService,
  PricePlansRestrictionsService,
  PricePlansRestrictionsModalService,
} from '../../../services';

@Component({
  selector: 'tablesready-waitlist-options',
  templateUrl: './waitlist-options.component.html',
  styleUrls: ['./waitlist-options.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WaitlistOptionsComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('noteOptions') noteOptionsRef?: FrontendDropdownPlainComponent;
  location: Location;
  account: Account;
  customerLabelOptions: string[];
  quotedLabelOptions: string[];
  autoRemoveOptions: {name: string, value: number}[];
  waitFieldsOptions: {name: string, id: string, hover: string}[];
  notificationTimeOptions = NOTIFICATION_TIME_OPTIONS;
  waitListSortByOptions: {name: string; value: string}[];
  defaultPageOptions: {name: string; value: number}[] = DEFAULT_PAGE_OPTIONS;

  waitlistOptionsForm: UntypedFormGroup;
  displayOptionsForm: UntypedFormGroup;

  private availableForStarterPlan = false;
  private availableForBusinessPlan = false;
  private subscriptions = new Subscription();

  constructor(
    private readonly alertsService: AlertsService,
    private readonly whoService: WhoService,
    private readonly fb: UntypedFormBuilder,
    private readonly cdr: ChangeDetectorRef,
    private readonly modalController: FrontendModalService,
    private readonly authRestaurantService: AuthRestaurantService,
    private readonly navService: NavigationService,
    private readonly pricePlansRestrictionsService: PricePlansRestrictionsService,
    private readonly pricePlansRestrictionsModalService: PricePlansRestrictionsModalService,
  ) {}

  ngAfterViewInit(): void {
    const navSub = this.navService.navEvents$
      .pipe(concatMap(() => {
        if (this.navService.currentUrl.includes('preferences/waitlist')) {
          return this.authRestaurantService.load();
        }

        return of(null);
      }))
      .subscribe();
    this.subscriptions.add(navSub);
  }

  ngOnInit(): void {
    this.customerLabelOptions = CUSTOMER_LABEL_OPTIONS;
    this.quotedLabelOptions = QUOTED_LABEL_OPTIONS;
    this.autoRemoveOptions = AUTO_REMOVE_OPTIONS;
    this.subscriptions.add(
      this.authRestaurantService.select()
        .pipe(
          filter(user => user.location !== undefined),
          tap(({location, account}) => {
            this.location = location;
            this.account = account;
            this.availableForStarterPlan = this.pricePlansRestrictionsService.availableForStarterPlan(account.subscriptionType, location.recurly);
            this.availableForBusinessPlan = this.pricePlansRestrictionsService.availableForBusinessPlan(account.subscriptionType, location.recurly);
            this.waitFieldsOptions = getWaitFieldsOptions(
              this.location.displayOptions.labels.customer,
            );
            this.waitListSortByOptions = [
              { name: `${this.location.displayOptions.labels.quoted} (default)`, value: 'quoted_time' },
              { name: 'Arrival Time', value: 'checkin_time' },
              { name: 'Name', value: 'name' },
            ];

            const floorPlanExistsInDefaultPageOption = this.defaultPageOptions.find(item => item.value === DefaultPage.floorPlan);
            if (!account.features.floorPlan && floorPlanExistsInDefaultPageOption) {
              this.defaultPageOptions = this.defaultPageOptions.filter(item => item.value !== DefaultPage.floorPlan);
            }

            if (account.features.floorPlan && !floorPlanExistsInDefaultPageOption) {
              this.defaultPageOptions.push({ value: DefaultPage.floorPlan, name: 'Floor Plan' });
            }
            let defaultPage = this.location.publicWaitlist.defaultPage || DefaultPage.waitlist;
            if (this.location.publicWaitlist.defaultPage === DefaultPage.floorPlan && !this.account.features.floorPlan) {
              defaultPage = DefaultPage.waitlist;
            }

            this.waitlistOptionsForm = this.fb.group({
              defaultPage: [defaultPage, [Validators.required]],
              autoRemove: [this.location.autoRemove || 12],
              default_quoted_time: [this.location.default_quoted_time, [Validators.required, Validators.min(0), Validators.pattern('[0-9]*')]],
              default_size: [this.location.default_size, [Validators.required, Validators.min(1), Validators.pattern('[0-9]*')]],
              noteOptions: [this.location.noteOptions || []],
              resourceSelection: [false],
              servingTraking: [false],
              waitFields: this.fb.group({
                quoted_time: this.location.waitFields.quoted_time || false,
                arrival_time: this.location.waitFields.arrival_time || false,
                wait_time: this.location.waitFields.wait_time || false,
                size: this.location.waitFields.size || false,
                note_column: this.location.waitFields.note_column || false,
                estimated_waits: this.location.waitFields.estimated_waits || false,
                position_in_line: this.location.waitFields.position_in_line || false,
                service: this.location.waitFields.service || false,
                resource: this.location.waitFields.resource || false,
                numericOderId: this.location.waitFields.numericOderId || false,
              }),
              waitListSortBy: [this.location.waitListSortBy],
            });

            this.waitFieldsOptions.unshift({
              name: 'Size',
              id: 'size',
              hover: 'Show the group size as a column in your waitlist.',
            });

            this.displayOptionsForm = this.fb.group({
              compact: [this.location.displayOptions.compact],
              footer: [this.location.displayOptions.footer],
              future: [this.location.displayOptions.future],
              togo: [this.location.displayOptions.togo || false],
              labels: this.fb.group({
                customer: [this.location.displayOptions.labels.customer, [Validators.required]],
                quoted: [this.location.displayOptions.labels.quoted, [Validators.required]]
              }),
              notificationTime: [this.location.displayOptions.notificationTime, [Validators.required]],
            });

            const togoSub = this.displayOptionsForm.get('togo')
              .valueChanges.pipe(
                filter((value: boolean) => value && !this.availableForBusinessPlan),
                switchMap(() => this.pricePlansRestrictionsModalService.display(this.authRestaurantService.get())),
                tap(() => {
                  this.displayOptionsForm.get('togo').setValue(false, { emitEvent: false });
                }),
              ).subscribe();
            this.subscriptions.add(togoSub);

            const estimatedWaitSub = this.waitlistOptionsForm.get('waitFields.estimated_waits')
              .valueChanges
              .pipe(
                filter((value: boolean) => value && !this.availableForStarterPlan),
                switchMap(() => this.pricePlansRestrictionsModalService.display(this.authRestaurantService.get(), true)),
                tap(() => {
                  this.waitlistOptionsForm.get('waitFields.estimated_waits')
                    .setValue(false, { emitEvent: false });
                }),
              ).subscribe();
            this.subscriptions.add(estimatedWaitSub);

            this.cdr.markForCheck();
          }),
        ).subscribe(),
    );
  }

  submitWaitlistOptionsForm(value) {
    if(!value.waitFields.size) {
      value.default_size = 1;
    }

    if (value.default_quoted_time >= 720) {
      this.modalController.create<QuotedTimeModal, { eventType: 'save' | 'cancel' }>(
        QuotedTimeModal,
        { defaultQuotedTime: value.default_quoted_time },
      )
        .pipe(
          take(1),
          filter(data => !!data),
        )
        .subscribe(({ eventType }) => {
          if (eventType === 'save') {
            this.updateWaitlistOptions(value);
            this.waitlistOptionsForm.markAsPristine();
          }
        });
    } else {
      this.updateWaitlistOptions(value);
    }
  }

  updateWaitlistOptions(value) {
    this.whoService.updateDefaults(value)
      .pipe(
        take(1),
        tap(() => {
          this.alertsService.add({
            type: IAlertTypes.success,
            heading: 'Success:',
            body: 'Your waitlist options settings successfully updated',
          });
        }),
        catchError((error) => {
          this.alertsService.add({
            type: IAlertTypes.danger,
            heading: 'Error:',
            body: String(error?.error.error),
          });

          return of(error);
        }),
      )
      .subscribe();
  }

  submitDisplayOptionsForm(value) {
    const customer_plural = value.labels.customer === 'party' ? 'parties' : `${value.labels.customer}s`,
      options = {
        compact: value.compact,
        footer: value.footer,
        future: value.future,
        togo: value.togo,
        labels: {
          brand: this.location.displayOptions.labels.brand,
          customer: value.labels.customer,
          customer_plural: customer_plural,
          quoted: value.labels.quoted,
          reservation: this.location.displayOptions.labels.reservation,
          reservation_plural: this.location.displayOptions.labels.reservation_plural,
        },
        notificationTime: value.notificationTime,
      };
    this.whoService.updateDisplayOptions(options).subscribe(
      (response) => {
        this.alertsService.add({
          type: IAlertTypes.success,
          heading: 'Updated:',
          body: response
        });
      },
      (error) => {
        this.alertsService.add({
          type: IAlertTypes.danger,
          heading: 'Error:',
          body: `${error.error.error}`
        });
      }
    )
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  trackBy(index: number): number {
    return index;
  }
}
